import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';

const rootElement = document.getElementById('root') as HTMLElement;

// react-snap bakes fully rendered HTML into build/<route>/index.html, which is
// what search engines and AI crawlers read (they see real headings/text instead
// of an empty <div id="root">).
//
// We deliberately mount with createRoot() rather than hydrateRoot(): React 18's
// hydration is strict about text-node boundaries, and browser HTML parsing
// merges adjacent text nodes that React's own render keeps separate (any JSX
// such as `text {" "}<span>` or `© {year}` produces those). Every mismatch made
// React abandon hydration and re-render the whole tree anyway, so hydrating
// bought nothing but console errors. A plain render is deterministic and the
// pre-rendered markup still serves crawlers.
// (Re-verified in Phase 3A: hydrateRoot throws React error #418 on every
// prerendered page and crashes react-snap after 8/35 routes — ruled out.)
//
// Phase 3A — CLS fix for lazy routes:
// On a direct load of a lazy route, the first createRoot render suspends at the
// route-level lazy() boundary and commits the Suspense fallback ("Loading
// page…"), which REPLACES the fully rendered prerendered DOM and collapses the
// page until the chunk arrives (measured: root HTML 45521→30398 chars, page
// height →900px, footer jumps to y≈218, CLS 0.51–0.82). Main-bundle routes
// swap atomically and measure CLS 0.003–0.025.
//
// Fix: before the first render, preload the chunk for the CURRENT route (the
// same module request strings App.tsx lazy() uses, so webpack reuses the
// identical chunks — no new files, no behavior change to Phase 2 splitting).
// The prerendered page stays visible while the chunk loads; React's first
// commit is then the real content (single atomic swap, no fallback flash).
// The Suspense boundary in App.tsx remains and still handles client-side
// navigation to not-yet-loaded routes. Safety net: if the chunk is slow
// (>1500ms) or fails, we render anyway and the old fallback behavior applies.
const LAZY_ROUTE_CHUNKS: Record<string, () => Promise<unknown>> = {
  "/services": () => import("./pages/ServicesPage/ServicesPage"),
  "/fleet": () => import("./pages/FleetPage/FleetPage"),
  "/airports": () => import("./pages/AirportsPage/AirportsPage"),
  "/get-a-quote": () => import("./pages/BookNow/BookNow"),
  "/privacy-policy": () => import("./pages/Policies/Policies"),
  "/terms-and-conditions": () => import("./pages/TermsAndConditionsPage/TermsAndConditionsPage"),
  "/contact": () => import("./pages/Contact/Contact"),
  "/services/corporate-travel": () => import("./pages/CorporateTravelPage/CorporateTravelPage"),
  "/services/weddings": () => import("./pages/WeddingsPage/WeddingsPage"),
  "/services/hourly-as-directed": () => import("./pages/HourlyPage/HourlyPage"),
  "/about": () => import("./pages/AboutPage/AboutPage"),
  "/news": () => import("./pages/NewsPage/NewsPage"),
  "/services/party-bus": () => import("./pages/PartyBusPage/PartyBusPage"),
  "/services/anniversaries": () => import("./pages/AnniversariesPage/AnniversariesPage"),
  "/services/bachelorette-party": () => import("./pages/BachelorettePartyPage/BachelorettePartyPage"),
  "/services/birthdays": () => import("./pages/BirthdaysPage/BirthdaysPage"),
  "/services/concerts": () => import("./pages/ConcertsPage/ConcertsPage"),
  "/services/cruise-port": () => import("./pages/CruisePortPage/CruisePortPage"),
  "/services/destination": () => import("./pages/DestinationPage/DestinationPage"),
  "/services/funerals": () => import("./pages/FuneralsPage/FuneralsPage"),
  "/services/group-events": () => import("./pages/GroupEventsPage/GroupEventsPage"),
  "/services/night-on-the-town": () => import("./pages/NightOnTheTownPage/NightOnTheTownPage"),
  "/services/prom": () => import("./pages/PromPage/PromPage"),
  "/services/school-events": () => import("./pages/SchoolEventsPage/SchoolEventsPage"),
  "/services/sports-events": () => import("./pages/SportsEventsPage/SportsEventsPage"),
  "/services/wine-and-sightseeing": () => import("./pages/WineAndSightseeingPage/WineAndSightseeingPage"),
  "/service-areas": () => import("./pages/ServiceAreasPage/ServiceAreasPage"),
};

function preloadCurrentRouteChunk(): Promise<unknown> {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const loader =
    LAZY_ROUTE_CHUNKS[path] ||
    // :slug detail pages share one chunk; App keys them by prefix.
    (path.startsWith("/services/airport-transportation/")
      ? () => import("./pages/AirportTransferPage/AirportTransferPage")
      : path.startsWith("/limo-service/")
        ? () => import("./pages/ServiceAreaPage/ServiceAreaPage")
        : undefined);
  if (!loader) {
    return Promise.resolve(null); // main-bundle route or 404: render immediately
  }
  return Promise.race([
    loader(),
    new Promise((resolve) => setTimeout(resolve, 1500)),
  ]).catch(() => null);
}

preloadCurrentRouteChunk().then((module) => {
  // Hand the already-loaded entry page to App so the FIRST render can mount it
  // directly (no lazy suspension -> no fallback frame -> no CLS spike).
  // See App.tsx gsRouteElement().
  if (module && (module as { default?: unknown }).default) {
    const path = window.location.pathname.replace(/\/+$/, "") || "/";
    (window as unknown as Record<string, unknown>).__gsInitialPage = {
      // App keys the :slug routes by prefix, not by full path.
      path: path.startsWith("/services/airport-transportation/")
        ? "/services/airport-transportation"
        : path.startsWith("/limo-service/")
          ? "/limo-service"
          : path,
      component: (module as { default: unknown }).default,
    };
  }
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </React.StrictMode>
  );
});
// No service worker is shipped with this build. Avoid registering a missing
// /sw.js file, which causes an unsupported-MIME request in the browser.

